import React from 'react';
import * as TWEEN from '@tweenjs/tween.js';
import { extent } from 'd3-array';
import { nest } from 'd3-collection';
import { scaleLinear } from 'd3-scale';
import { area, stack } from 'd3-shape';
import { Motion, spring } from 'react-motion'
import TweenedPath from './TweenedPath';

import sortBy from 'lodash.sortby';

import { metrics, colors, section, abbreviations } from './settings.js';
import scaleProjection from './scaleProjection.js';

import './Chart.css';

class StateChart extends React.Component {
  render() {
    let selectedState = null;
    if (this.props.tooltip) {
      if (this.props.tooltip.level === 'state') {
        selectedState = this.props.tooltip.key;
      }
    }

    const sorted = sortBy(this.props.states.slice(0), this.props.sortFn);

    const bars = this.props.states.map(s => {
      const index = sorted.findIndex(d => d.key === s.key)
      const x = this.props.xScale(index);
      return (
        <Motion key={s.key} style={{ x: spring(x) }}>
          {style => {
            let yOffset = metrics.chartHeight - (metrics.padding * 2);
            return (
              <g
                key={s.key}
                className='stateGroup'
                transform={`translate(${style.x}, 0)`}
                onMouseOver={this.props.openTooltip(Object.assign({ bumpUp: true }, s))}
                onMouseOut={this.props.closeTooltip}
                onClick={() => this.props.setGeo(s.key)}
              >
                <rect
                  fill='transparent'
                  y={metrics.padding * 3}
                  width={this.props.stateWidth}
                  height={metrics.chartHeight - (metrics.padding * 5)}
                />
                {
                  s.groups.filter(g => g.key !== 'cap').map(g => {
                    yOffset -= this.props.yScale(g.count);
                    return (
                      <Motion
                        style={{
                          yOffset: spring(yOffset),
                          height: spring(this.props.yScale(g.count))
                        }}
                        key={g.key}
                      >
                        {
                          style => (
                            <rect
                              key={g.key}
                              transform={`translate(0, ${style.yOffset})`}
                              width={this.props.stateWidth}
                              height={style.height}
                              fill={g.settings.color}
                              stroke='#262626'
                              strokeWidth={0.5}
                            />
                          )
                        }
                      </Motion>
                    )
                  })
                }
                <text
                  x={this.props.stateWidth / 2}
                  y={metrics.chartHeight - metrics.padding}
                  className='chartLabel compactFont'
                  fill={selectedState === s.key ? 'white' : '#666'}
                  opacity={this.props.graphTween}
                >
                  {abbreviations[s.key]}
                </text>
              </g>
            );
          }}
        </Motion>
      )
    });
    return bars;
  }
}

class StackChart extends React.Component {
  render() {
    let selectedDate = null;
    if (this.props.tooltip) {
      if (this.props.tooltip.level === 'date' && this.props.tooltip.values.length) {
        const selectedDatum = this.props.tooltip.values[0];
        selectedDate = new Date(
          selectedDatum.datePieces[2],
          selectedDatum.datePieces[0] - 1
        ).valueOf();
      }
    }
    const rectSize = this.props.bottomChart.getBoundingClientRect();
    const maxX = this.props.xScale.range()[1];
    return (
      <g>
        {
          this.props.byDate.map((d, i) => {
            let yearLabel = '';
            if (i === 0 || d.year !== this.lastYear) {
              const scaleX = this.props.xScale(new Date(d.year, 0).valueOf());
              const fixRight = scaleX + 96 > maxX;
              // don't let the year go off the right edge
              const yearX = fixRight ? maxX - 96 : scaleX;
              this.lastYear = d.year;
              yearLabel = (
                <text
                  key={d.year}
                  x={yearX}
                  y={(metrics.padding * 4.2)}
                  fill={colors.grey.dark}
                  fontSize={72}
                  textAnchor='left'
                  className='compactFont'
                  opacity={this.props.graphTween}
                >
                  {d.year}
                </text>
              )
            }
            return (
              <g key={d.key}>
                <text
                  key={d.key}
                  x={this.props.xScale(d.date)}
                  y={metrics.chartHeight - metrics.padding}
                  fill={selectedDate === d.date ? 'white' : '#666'}
                  className='chartLabel compactFont'
                  opacity={this.props.graphTween}
                >
                  {d.label}
                </text>
                {yearLabel}
              </g>
            )
          })
        }
        {
          this.props.timeseries.map(g => {
            return <TweenedPath key={g.key} path={this.props.areaGenerator(g)} fill={g.settings.color} />
          })
        }
        <rect
          x={metrics.padding / 2}
          y={metrics.padding}
          width={rectSize.width - metrics.padding * 2}
          height={rectSize.height - metrics.padding * 2}
          fill='transparent'
          onMouseOver={this.props.openTooltip(this.props.timeseries, this.props.xScale, this.props.bottomChart)}
          onMouseMove={this.props.openTooltip(this.props.timeseries, this.props.xScale, this.props.bottomChart)}
          onMouseOut={this.props.closeTooltip}
        />
        {
          (selectedDate !== null) ? (
            <line
              x1={this.props.xScale(selectedDate)}
              x2={this.props.xScale(selectedDate)}
              y1={this.props.yScale(0) + metrics.padding}
              y2={this.props.yScale(this.props.tooltip.values.length) + metrics.padding}
              stroke='black'
              strokeWidth='1'
              pointerEvents='none'
            />
          ) : ''
        }
      </g>
    )
  }
}

export default class Chart extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      sortKey: 'key',
      sortFn: 'key',
      graphTween: 0,
    };
    const tweenedVariable = { graphTween: 0 }
    this.tween = new TWEEN.Tween(tweenedVariable)
      .onUpdate(() => {
        this.setState({ graphTween: tweenedVariable.graphTween })
      })
      .easing(TWEEN.Easing.Quartic.InOut)

    this.selectedGeo = null;

    this.chart = {
      width: metrics.filterWidth - metrics.padding,
      height: 64,
      types: [
        {
          key: 'state',
          label: 'State-by-State',
        },
        {
          key: 'time',
          label: 'Change Over Time',
        },
      ],
      sorts: [
        {
          key: 'key',
          label: 'Name',
        },
        {
          key: 'count',
          label: 'Count',
        },
      ],
      scales: {
        state: scaleLinear().range([metrics.padding, this.props.width - (metrics.filterWidth + metrics.padding * 2)]),
        date: scaleLinear().range([metrics.padding, this.props.width - (metrics.filterWidth + metrics.padding * 2)]),
        y: scaleLinear().range([0, metrics.chartHeight - (metrics.padding * 3)]),
        ya: scaleLinear().range([metrics.chartHeight - (metrics.padding * 3), 0]),
      },
    };

    this.chart.scales.date.domain(extent(this.props.dateRange.map(d => d.date)));
    this.chart.scales.state.domain([0, this.props.states.length]);
    this.chart.scales.stateWidth = (
      this.chart.scales.state(1) - this.chart.scales.state(0) - (metrics.padding / 4)
    );

    this.areaGenerator = area()
      .x((d) => this.chart.scales.date(d.data.date))
      .y0((d) => this.chart.scales.ya(d[0]) + metrics.padding)
      .y1((d) => this.chart.scales.ya(d[1]) + metrics.padding);
  }

  componentDidMount() {
    this._openTimeout = setTimeout(() => this._openChart(), 200)
  }

  componentWillUnmount() {
    this.tween.stop()
    clearTimeout(this._openTimeout)
  }

  _openChart = () => {
    this.tween.stop()
    this.tween.to({ graphTween: 1 }, 1000)
      .onComplete(() => {})
      .start()
  }

  componentWillUpdate(nextProps) {
    if (JSON.stringify(nextProps.stateExtent) !== JSON.stringify(this.chart.scales.y.domain())) {
      this.chart.scales.y.domain(nextProps.stateExtent);
    }
    if (nextProps.width !== this.props.width) {
      this.chart.scales.state.range(
        [metrics.padding, nextProps.width - (metrics.filterWidth + metrics.padding * 2)]
      );
      this.chart.scales.date.range(
        [metrics.padding, nextProps.width - (metrics.filterWidth + metrics.padding * 2)]
      );
      this.chart.scales.stateWidth = (
        this.chart.scales.state(1) - this.chart.scales.state(0) - (metrics.padding / 4)
      );
    }
    if (nextProps.selected.geo !== this.selectedGeo) {
      this.selectedGeo = nextProps.selected.geo;
      this.updateMiniMap(nextProps.selected.geo);
      this.updateStackChart(nextProps);
    } else if (JSON.stringify(nextProps.states) !== JSON.stringify(this.props.states)) {
      this.updateStackChart(nextProps);
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.selected.type !== this.props.selected.type) {
      this._openTimeout = setTimeout(this._openChart, 1)
    }
  }

  updateMiniMap = (geo) => {
    const polygon = (geo === 'United States') ? this.props.map.outline : (
      this.props.map.states.features.filter(s => {
        return s.properties.NAME === geo;
      })[0].geometry
    );
    const mini = scaleProjection(polygon, this.chart.width, this.chart.height);
    this.miniPath = mini.path;
    this.miniProjection = mini.projection;
    this.generatedMiniPath = this.miniPath(polygon);
  }

  updateStackChart = (props) => {
    const stackFunction = stack()
      .keys(
        sortBy(
          props.group.map(d => d.name),
          (d) => section(d).order,
        )
      );

    this.byDate = nest()
      .key(d => new Date(d.datePieces[2], d.datePieces[0] - 1, 1))
      .entries(props.states.slice().reduce((a, v) => a.concat(v.values), []))
      .map(d => {
        const date = new Date(d.key);
        d.values = d.values.filter(v => {
          if (props.selected.geo === 'United States') {
            return true;
          }
          return v.State === props.selected.geo;
        });
        const datum = {
          key: d.key,
          date: date.valueOf(),
          year: date.getFullYear(),
          label: date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
          values: d.values,
        };
        nest().key(props.keyFunction).entries(d.values).forEach(v => {
          datum[this.getChartType(v.key)] = v.values.length;
        });
        props.group.map(d => d.name).forEach(k => {
          if (!datum.hasOwnProperty(k)) {
            datum[k] = 0;
          }
        })
        return datum;
      });

    // handle potential months with 0 incidents
    const missingMonths = props.dateRange.filter(d => !this.byDate.find(b => b.date === d.value));
    missingMonths.forEach(m => {
      const datum = {
        key: m.date.toString(),
        date: m.value,
        year: m.date.getFullYear(),
        label: m.date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
        values: [],
      };
      props.group.map(d => d.name).forEach(k => {
        datum[k] = 0;
      });
      this.byDate.push(datum);
    });

    this.byDate = sortBy(this.byDate, 'date');

    this.timeseries = stackFunction(this.byDate).map(g => {
      g.settings = section(g.key);
      return g;
    });

    this.chart.scales.ya.domain(
        extent(this.timeseries.reduce((a, v) => a.concat(v), []).reduce((a, v) => a.concat(v), []))
      );
  }

  getChartType = (g) => {
    const cleanTypeMap = {
      'banner-Public': 'Banners',
      'flier-Campus': 'Campus Flyers',
      'flier-Public': 'Public Flyers',
    };
    if (Object.keys(cleanTypeMap).includes(g)) {
      return cleanTypeMap[g];
    }
    return g;
  }

  _toggleType = (type) => {
    return () => {
      if (type.key === this.props.selected.type) {
        return
      }
      this.tween.stop()
      this.tween.to({ graphTween: 0 }, 300)
        .onComplete(() => this.props.setType(type.key))
        .start()

    }
  }

  render() {
    const defaultHeight = metrics.chartHeight - (metrics.padding * 3)
    this.chart.scales.y.range([0, defaultHeight * this.state.graphTween])
    this.chart.scales.ya.range([defaultHeight, defaultHeight - defaultHeight * this.state.graphTween])

    const typeSelector = (this.props.selected.geo === 'United States') ? (
        <div className='radioGroup'>
          {
            this.chart.types.map(t => {
              return (
                <div key={t.key} className='inlineControl radio'>
                  <input
                    type='radio'
                    id={t.key}
                    key={t.key}
                    value={t.key}
                    name={t.key}
                    checked={this.props.selected.type === t.key}
                    onChange={this._toggleType(t)}
                  />
                  <label htmlFor={t.key}>{t.label}</label>
                </div>
              )
            })
          }
        </div>
      ) : (
        <div
          key='national'
          className='inlineControl national'
          onClick={() => this.props.setGeo('United States')}
        >
          <label>View National</label>
        </div>
      )
    const sortSelector = (this.props.selected.type === 'state') ? (
      <div className='inlineControl'>
        <label htmlFor='sortSelect'>Sort by</label>
        <select
          id='sortSelect'
          onChange={(e) => {
            const sortKey = e.target.value;
            let sortFn = sortKey;
            if (sortKey === 'count') {
              sortFn = (d) => { return -d.values.length };
            }
            this.setState({ sortKey, sortFn });
          }}
          value={this.state.sortKey}
        >
        {
          this.chart.sorts.map(t => {
            return <option key={t.key} value={t.key}>{t.label}</option>
          })
        }
        </select>
      </div>
    ) : '';
    const displayChart = (this.props.selected.type === 'state') ? (
      <StateChart
        states={this.props.states}
        sortFn={this.state.sortFn}
        xScale={this.chart.scales.state}
        yScale={this.chart.scales.y}
        stateWidth={this.chart.scales.stateWidth}
        tooltip={this.props.tooltip}
        openTooltip={this.props.openTooltip}
        closeTooltip={this.props.closeTooltip}
        setGeo={this.props.setGeo}
        graphTween={this.state.graphTween}
      />
    ) : (
      <StackChart
        byDate={this.byDate}
        timeseries={this.timeseries}
        xScale={this.chart.scales.date}
        yScale={this.chart.scales.ya}
        areaGenerator={this.areaGenerator}
        tooltip={this.props.tooltip}
        openTooltip={this.props.openDateTooltip}
        closeTooltip={this.props.closeTooltip}
        bottomChart={this._bottomChart}
        graphTween={this.state.graphTween}
      />
    );
    return (
      <div
        className='container'
        style={{
          background: '#262626',
          width: this.props.width,
          height: metrics.chartHeight,
          marginTop: 0,
        }}
      >
        <div
          style={{
            display: 'inline-block',
            width: this.chart.width,
            height: metrics.chartHeight - (metrics.padding * 2),
            margin: metrics.padding,
            marginRight: 0,
          }}
        >
          <div className='chartTitle compactFont'>
            {this.props.selected.geo}
          </div>
          <svg
            style={{
              display: 'inline-block',
              position: 'relative',
              margin: `${(metrics.padding / 4)}px 0`,
              width: this.chart.width,
              height: this.chart.height,
            }}
          >
            <path
              fill='none'
              stroke={colors.grey.lightest}
              d={this.generatedMiniPath}
            />
          </svg>
          {typeSelector}
        </div>
        <svg
          ref={c => (this._bottomChart = c)}
          style={{
            display: 'inline-block',
            width: this.props.width - metrics.filterWidth,
            height: metrics.chartHeight,
            cursor: 'pointer',
          }}
        >
          {displayChart}
        </svg>
        <div className='sortOverlay'>
          {sortSelector}
        </div>
      </div>
    )
  }
}
